import {Injectable} from "@angular/core";
import * as moment from "moment";
// TODO - javascript moment will parse garbage without issue and cause weird date compares. also need to force UTC
// TODO - implement required field checking
// TODO - min / max / requireed validation
// TODO - type on metadata once it's fleshed out
// TODO - searching
// TODO - import / export to excel (not a requirement)
// TODO - I want to change how the currency is handled. Currently if it's changed, that is not reflected and it'll
//        mess with export if that's implemented at a later date
// TODO - export could be made simplier if it tracked a pointer to the original data
// TODO - refactor - there are a lot of places where the name of the local variable for the list structure and the
//         static initializer are the same causing potential confussion (eg dashboardIssueData)

const DEFAULT_PAGE = 1;
const MIN_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;
const UNEXPECTED_ERROR = "An unexpected error has occurred";
const UNINTIALIZED_ERROR = "ERROR - Editing is not initialized";
const APPEND_DISABLED_ERROR = "ERROR - Add has not been enabled";
const MEANINGLESS_SORT = (A: any, B: any) => <number>0; // eslint-disable-line

@Injectable()
export class ListStructure {
  // Identification
  public name: string = "item"; // Name of the items we are dealing with
  public editTitle: string = "Edit Item";
  public title: string;
  public defaults = {
    editable: true,
    removable: true,
    newRowDefaults: {},
    ngClass: undefined,
  };

  public editable: boolean = false; // Allow to edit rows
  public removable: boolean = false; // Allow to remove rows
  public appendable: boolean = false; // Allow the user to add rows
  public ordered: boolean = false; // Display ordering information
  public reorderable: boolean = false; // Allow altering of the order

  // Status information
  public error = null; // Any errors that have been seen attempting to process the data
  public successMessage = null; // Non-error messages (typically that the structure is dirty
  public loading = true; // Has data been loaded yet

  // Sort data
  public sortOrder = {};
  public sort: any = MEANINGLESS_SORT;
  // Pagination data
  public showSearch: boolean = false;
  public showLimit: boolean = true;
  public searchText: string = "";
  public currentPage: number = DEFAULT_PAGE;
  public pageSize: number = DEFAULT_PAGE_LIMIT;
  public maxPages: number = 0;
  public totalCount: number = 0;
  public totalCountUnfiltered: number = 0;

  // Model Data
  public metadata = [];

  // The items we are managing
  public exportData = []; // The data we will export
  public sortedData = []; // Data sorted and augmented with the index, row, editable, etc
  public sortedFilteredData = []; // Data from sorted Data that appears on the current page
  public pageData = []; // Final transformation. This is the data we're displaying on this page
  public dirty = false; // Have any changes been made

  public today = moment()
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);

  public now = moment();

  // Injected services
  private listEditorService;

  private callbacks: any = {}; // Methods to call on certain actions

  // HELPER METHODS
  public resetStatus() {
    // PURPOSE - place this list back into its initial state as far as status is concerned (not data)
    this.loading = true;
    this.error = null;
    this.successMessage = null;
  }

  public setErrorString(error: string = UNEXPECTED_ERROR) {
    // PURPOSE - create an error
    this.loading = false;
    this.error = new Error(error);
  }

  public toString() {
    return JSON.stringify({
      name: this.name,
      defaults: this.defaults,
      editable: this.editable,
      removable: this.removable,
      appendable: this.appendable,
      error: (this.error ? this.error.toString() : ""),
      successMessage: this.successMessage,
      loading: this.loading,
      sortOrder: this.sortOrder,
      searchText: this.searchText,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      maxPages: this.maxPages,
      totalCount: this.totalCount,
      metadata: this.metadata,
      exportData: this.exportData,
      sortedData: this.sortedData,
      sortedFilteredData: this.sortedFilteredData,
      pageData: this.pageData,
      dirty: this.dirty,
    });
  }

  // CORE FUNCTIONALITY
  public updateListData(newData, dirty = null) {
    // PURPOSE: full update based on newData (page load typically)
    if (dirty === true) this.makeDirtyHelper();
    else if (dirty === false) this.dirty = dirty;

    this.generateSortOrderClosure(); // Make sure the sort is up to date
    this.exportData = newData;
    this.createSortedData();
  }

  private refreshLoadingStatus() {
    // Have we been initialized. We need to be displaying at least one item and have a list editor service injected
    this.loading = !(this.listEditorService && this.exportData && this.metadata && this.metadata.length > 0);
  }

  public initialize(
    generalData: any,
    metadata: any,
    listEditorService: any,
    listData: any,
    callbacks: any = null,
  ) {
    // Load general information
    if (generalData) {
      this.name = generalData.name || this.name;
      this.title = generalData.title || this.title;
      this.defaults = generalData.defaults || this.defaults;
      if (generalData.editable !== undefined) this.editable = generalData.editable;
      if (generalData.removable !== undefined) this.removable = generalData.removable;
      if (generalData.appendable !== undefined) this.appendable = generalData.appendable;
      if (generalData.sortOrder) this.sortOrder = generalData.sortOrder;
      if (generalData.ordered) this.ordered = generalData.ordered;
      if (generalData.reorderable) this.reorderable = generalData.reorderable;
      if (generalData.showLimit !== undefined) this.showLimit = generalData.showLimit;
      if (generalData.pageSize) this.pageSize = generalData.pageSize;
      if (generalData.editTitle) this.editTitle = generalData.editTitle;
    }

    // Load the callbacks
    if (callbacks) {
      Object.keys(callbacks).forEach((key) => { this.callbacks[key] = callbacks[key]; });
    }

    // Load the model information
    if (metadata) {
      this.metadata = metadata;
      // NOTE this line below is commented out because the feature is not implemented yet
      // TODO this.showSearch = metadata.some(info => info.searchable);
    }

    // Load data
    if (listData) {
      this.updateListData(listData);
    }

    if (listEditorService) {
      this.listEditorService = listEditorService;
    }
    this.refreshLoadingStatus();
  }

  private rowIsInFilter(data) { // eslint-disable-line
    // PURPOSE - to implement searching. Currently a pass through placeholder
    return true;
  }

  private rowIsInPage(index) {
    return (index >= (this.currentPage - 1) * this.pageSize) &&
      (index < this.currentPage * this.pageSize);
  }

  private updateSortedFilteredData() {
    // PURPOSE: Handle any changes required because of a change to sortedData
    this.sortedFilteredData = this.sortedData
      .filter(data => this.rowIsInFilter(data));
    this.updatePageData();
  }

  private recalculatePageInformation() {
    this.totalCount = this.sortedFilteredData.length;
    this.totalCountUnfiltered = this.exportData.length;
    this.maxPages = Math.ceil(this.totalCount / (+this.pageSize));
    if (this.currentPage > this.maxPages || this.currentPage < MIN_PAGE) {
      this.currentPage = DEFAULT_PAGE;
    }
  }

  private updatePageData() {
    // PURPOSE: Figure out what items are on this page
    this.recalculatePageInformation();
    this.pageData = this.sortedFilteredData.filter((obj, index) => this.rowIsInPage(index));
  }

  private augmentData(data) {
    // PURPOSE - this method is used to create the strings that represent this row on display, sort, etc
    const augmentedData = {};
    this.metadata.forEach((info) => {
      const item = (info.field === "self") ? data.row : data.row[info.field];

      const display = (info.stringFunctions && info.stringFunctions.display) ?
        info.stringFunctions.display({
          info,
          item,
          field: info.field,
          self: data.row,
          today: this.today,
          now: this.now,
        }) :
        item;

      const search = (info.stringFunctions && info.stringFunctions.search) ?
        info.stringFunctions.search({
          info,
          item,
          field: info.field,
          self: data.row,
          today: this.today,
          now: this.now,
        }) :
        display;

      const sort = (info.stringFunctions && info.stringFunctions.sort) ?
        info.stringFunctions.sort({
          info,
          item,
          field: info.field,
          self: data.row,
          today: this.today,
          now: this.now,
        }) :
        display;

      const exportedValue = (info.stringFunctions && info.stringFunctions.export) ?
        info.stringFunctions.export({
          info,
          item,
          field: info.field,
          self: data.row,
          today: this.today,
          now: this.now,
        }) :
        item;

      augmentedData[info.field] = {item, display, search, sort, exportedValue};
    });
    data.augmentedData = augmentedData;
  }

  private reAugmentSortedData() {
    this.sortedData.forEach((data) => {
      this.augmentData(data);
    });
  }

  private reSortSortedData() {
    // PURPOSE: This is for when there are changes to the order, but not the data is still valid
    this.sortedData
      .sort(this.sort); // Original data is sorted so that paging comes out right
    this.updateSortedFilteredData();
  }

  private createSortedData() {
    // PURPOSE: Handle and changes that need to occur because of a change to exportData
    this.sortedData = this.exportData // Copy/augment the new data
      .map((row, index) => {
        let ngClass;
        if (this.defaults && this.defaults.ngClass) {
          if (typeof this.defaults.ngClass === "function") {
            ngClass = this.defaults.ngClass({row, index, today: this.today, now: this.now});
          } else {
            ngClass = this.defaults.ngClass;
          }
        }

        return {
          index,
          row,
          // Default to true unless the default is explicitly false
          editable: !(this.defaults && this.defaults.editable === false),
          // Default to true unless the default is explicitly false
          removable: !(this.defaults && this.defaults.removable === false),
          ngClass,
        };
      });
    // Now augment the data
    this.reAugmentSortedData();
    this.reSortSortedData(); // Now actually sort it. (this updates filtered data (next step in chain
  }

  private generateSortOrderClosure() {
    // PURPOSE - this sets up the sort closure after a change to the sort order has been made
    // Done this way so that its not continuously be recomputed for every step in a n*lg(n) sort
    const forwardIndex = (A: any, B: any) => {
      const {index: idxA} = A;
      const {index: idxB} = B;
      return (idxA - idxB) as number;
    }; // In index order (a default

    if (!this.sortOrder) {
      return forwardIndex;
    }

    const sortOrderInfo = Object.keys(this.sortOrder)
      .map(x => ({field: x, ...this.sortOrder[x]})) // Object to array
      .sort((a, b) => (a.precedence - b.precedence)); // Order it in precedence order

    this.sort = ((A: any, B: any) => {
      // RETURNS: a closure that looks at each of the fields in sequence
      // closure returns: -1 of A comes before B. 1 if B comes before A. 0 if they are the same
      const {augmentedData: dataA, row: rowA, index: indexA} = A;
      const {augmentedData: dataB, row: rowB, index: indexB} = B;
      let found = 0;

      if (!sortOrderInfo || sortOrderInfo.length === 0) return forwardIndex; // Default if nothing is set
      sortOrderInfo.forEach(({field, order}) => {
        if (found) return;

        let fieldA = (field === "index") ?
          indexA :
          (dataA[field] ? dataA[field].sort : rowA[field]);

        if (typeof fieldA === "string") fieldA = fieldA.toUpperCase(); // String comparisons as case insensitive

        let fieldB = (field === "index") ?
          indexB :
          (dataB[field] ? dataB[field].sort : rowB[field]);

        if (typeof fieldB === "string") fieldB = fieldB.toUpperCase(); // String comparisons as case insensitive

        // NOTE - It's a bit counter intuitive but assending refers to higher values being later which corrisponds
        //        to a downward arrow (the arrow points towards larger values)
        if (order === "ascending") {
          if (fieldA < fieldB) {
            found = -1;
          } else if (fieldA > fieldB) {
            found = 1;
          } else if ((typeof fieldA === "boolean") && (typeof fieldB === "boolean") && (fieldA !== fieldB)) {
            found = (fieldA === false ? -1 : 1); // false comes before true in ascending (arbitrary)
          }
        } else if (order === "descending") {
          if (fieldA < fieldB) {
            found = 1;
          } else if (fieldA > fieldB) {
            found = -1;
          } else if ((typeof fieldA === "boolean") && (typeof fieldB === "boolean") && (fieldA !== fieldB)) {
            found = (fieldA === false ? 1 : -1);
          }
        }
      });

      return found as number;
    });

    return undefined;
  }

  public removeItem(indexOnPage) {
    const item = this.sortedFilteredData[indexOnPage];
    if (!this.editable || !item.editable) {
      this.error = new Error(`Deletion of ${this.name} not allowed`);
      return;
    }

    this.exportData.splice(item.index, 1); // Remove from our copy of the original data
    this.createSortedData();
    this.makeDirtyHelper();
  }

  public itemUp(index) {
    // PURPOSE - move this item to a lower index on the list
    if (index <= 0 || index >= this.exportData.length) return;
    const temp = this.exportData[index - 1];
    this.exportData[index - 1] = this.exportData[index];
    this.exportData[index] = temp;
    this.createSortedData(); // Maybe in-efficient but redo the sorted array
    if (this.ordered) this.makeDirtyHelper(); // If this is an ordered list then this is considered a change
  }

  public itemDown(index) {
    // PURPOSE - move this item to a higher index on the list
    if (index < 0 || index >= this.exportData.length - 1) return;
    const temp = this.exportData[index];
    this.exportData[index] = this.exportData[index + 1];
    this.exportData[index + 1] = temp;
    this.createSortedData(); // Maybe in-efficient but redo the sorted array
    if (this.ordered) this.makeDirtyHelper(); // If this is an ordered list then this is considered a change
  }

  public modifySortOrder(field) {
    const incrementPrecedence = () => {
      Object.keys(this.sortOrder)
        .forEach((key) => {
          this.sortOrder[key].precedence += 1;
        });
    };
    const incrementPrecedenceBelow = (precedence) => {
      Object.keys(this.sortOrder)
        .forEach((key) => {
          if (this.sortOrder[key].precedence < precedence) {
            this.sortOrder[key].precedence += 1;
          }
        });
    };

    if (!this.sortOrder[field]) {
      // This field is not in the current order at all
      incrementPrecedence();
      this.sortOrder[field] = {
        order: "ascending",
        precedence: 1,
      };
    } else if (this.sortOrder[field].precedence === 1) {
      // This is already the current leader so we"re just swapping the order
      if (this.sortOrder[field].order === "ascending") this.sortOrder[field].order = "descending";
      else this.sortOrder[field].order = "ascending";
    } else {
      // this one is in the middle of the list, so make it the lead item and shuffle items below it.
      incrementPrecedenceBelow(this.sortOrder[field].precedence);
      this.sortOrder[field].precedence = 1;
    }

    this.generateSortOrderClosure(); // Update the sorting closure
    this.reSortSortedData(); // Re-Sort to reflect the changes
  }

  private createEditObjectHelper(indexOnPage) {
    const data = {};
    const editableData = this.pageData[indexOnPage].augmentedData;

    this.metadata.forEach((info) => {
      if (editableData[info.field].item !== undefined) {
        data[info.field] = editableData[info.field].item;
      }
    });

    return data;
  }

  private saveEditObjectHelper(result, indexOnPage) {
    if (result) {
      let isDirty = false;
      const index = this.pageData[indexOnPage].index;
      this.metadata.filter(info => info.editable)
        .forEach((info) => {
          if (result[info.field] !== undefined) {
            isDirty = true;
            if (info.field === "self") {
              this.exportData[index] = result.self;
            } else {
              this.exportData[index][info.field] = result[info.field];
            }
          }
          this.createSortedData(); // Does a bit extra work
        });
      if (isDirty) this.makeDirtyHelper();
    }
  }

  private saveAddObjectHelper(result) {
    if (result) { // Note I'm not allowing adding an empty object
      if (this.metadata.some(info => info.field === "self")) {
        this.exportData.push(result.self);
      } else {
        this.exportData.push(result);
      }
      this.createSortedData(); // Does a bit extra work
      this.makeDirtyHelper();
    }
  }

  private editTitleHelper() {
    return this.editTitle || `Edit ${this.name}`;
  }

  public editListItem(indexOnPage) {
    if (!this.listEditorService) {
      this.setErrorString(UNINTIALIZED_ERROR);
      return;
    }

    // NOTE we have to do things this way to handle self-lists and such
    const data = this.createEditObjectHelper(indexOnPage);
    this.listEditorService.modifyListItem(data, this.metadata, this.editTitleHelper())
      .subscribe((result) => {
        this.saveEditObjectHelper(result, indexOnPage);
      });
  }

  public itemToString(item, metadataInfo) {
    if (!metadataInfo) return item.toString();
    return undefined;
  }

  public export() {
    return this.exportData;
  }

  public add() {
    if (!this.appendable) {
      this.setErrorString(APPEND_DISABLED_ERROR);
      return;
    }

    let newRow;
    if (typeof this.defaults.newRowDefaults === "function") {
      newRow = (this.defaults.newRowDefaults as Function)(this.exportData, this.metadata);
    } else if (this.defaults.newRowDefaults) {
      newRow = this.defaults.newRowDefaults;
    } else {
      newRow = {};
    }

    this.listEditorService.modifyListItem(newRow, this.metadata, this.editTitleHelper())
      .subscribe((result) => {
        this.saveAddObjectHelper(result);
      });
  }

  public onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.updateSortedFilteredData();
  }

  public getDisplayableMetadata() {
    return this.metadata.filter(it => !it.disableListDisplay);
  }

  private makeDirtyHelper() {
    this.dirty = true;
    if (this.callbacks.onChange) {
      this.loading = true;
      try {
        this.callbacks.onChange();
      } catch (e) {
        this.error = e;
      }
      this.loading = false;
    }
  }

  constructor() {}
}
